import type { MutableRefObject } from "react";
import { detectBoundary, exitFormattedElement } from "../boundary-navigator";
import {
  COLLAPSIBLE_TEXT_SELECTOR,
  LIVEBLOCKS_COMMENTS_SELECTOR,
} from "../constants";
import type { FroalaEditorInstance } from "../types";

/** Dialog element IDs that should prevent editor focus when open */
const DIALOG_IDS_TO_IGNORE = ["UWCpUVbCFXhcWew", "share-dialog"] as const;

/** Style properties to inherit from previous paragraph on Enter */
const INHERITABLE_BLOCK_STYLES = [
  "font-size",
  "color",
  "font-family",
  "font-weight",
] as const;

export const createEditorKeydownHandler = () => {
  return function (this: FroalaEditorInstance, e: KeyboardEvent) {
    // START: Select for RKPI
    if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const sel = document.getSelection();
      if (sel) {
        if (sel.rangeCount === 0) return;
        const r = sel.getRangeAt(0).cloneRange();

        if (r.startOffset !== r.endOffset) return;

        let sibling: Node | null = null;
        if (e.key === "ArrowLeft" && r.startOffset === 0) {
          sibling = r.startContainer.previousSibling;
        } else if (
          e.key === "ArrowRight" &&
          r.startOffset === r.startContainer.textContent?.length
        ) {
          sibling = r.startContainer.nextSibling;
        }

        if (
          !(sibling instanceof HTMLElement) ||
          !sibling.firstChild ||
          !sibling.closest("[data-rkpi-active]")
        ) {
          // Phase 3: Format boundary navigation
          // Only run if RKPI handler didn't handle it
          if (sel.isCollapsed) {
            const boundary = detectBoundary(sel);
            if (boundary.isAtBoundary && boundary.formattedElement) {
              // ArrowRight at end of formatted element → exit after
              if (e.key === "ArrowRight" && boundary.side === "end") {
                e.preventDefault();
                exitFormattedElement(sel, boundary);
                return;
              }
              // ArrowLeft at start of formatted element → exit before
              if (e.key === "ArrowLeft" && boundary.side === "start") {
                e.preventDefault();
                exitFormattedElement(sel, boundary);
                return;
              }
            }
          }
          return;
        }

        e.preventDefault();
        const nRange = new Range();
        nRange.selectNode(sibling.firstChild);
        sel.removeAllRanges();
        sel.addRange(nRange);
      }
    }

    // Backspace/Delete is NOT handled here. Froala runs this config `keydown`
    // callback AFTER its own keys module has already performed the deletion
    // (events.trigger calls module handlers first, opts.events last), so the
    // caret here is the post-deletion one: an atom next to it is the
    // neighbour of the character just deleted, not the target (bug
    // 13168657272). Pills, variable blocks, icons/emoji, RKPI counters and
    // fast-versioning tokens are deleted whole by `attachAtomicPills`
    // (shared/utils/pill-atomic.ts), whose document capture listener sees the
    // caret before Froala does.

    // Phase 8: Style inheritance on Enter
    if (e.key === "Enter") {
      const editor = this;
      requestAnimationFrame(() => {
        const sel = document.getSelection();
        if (!sel || sel.rangeCount === 0) return;
        const range = sel.getRangeAt(0);
        let newBlock =
          range.startContainer instanceof HTMLElement
            ? range.startContainer
            : range.startContainer.parentElement;

        // Walk up to find the block element
        while (
          newBlock &&
          !["P", "H2", "H3", "LI"].includes(newBlock.tagName)
        ) {
          if (newBlock.classList?.contains("fr-view")) break;
          newBlock = newBlock.parentElement;
        }

        if (!newBlock || !["P", "H2", "H3", "LI"].includes(newBlock.tagName))
          return;

        // Only apply to empty new blocks
        const textContent = newBlock.textContent?.trim() ?? "";
        if (textContent.length > 0) return;

        const prevBlock = newBlock.previousElementSibling;
        if (!prevBlock || !(prevBlock instanceof HTMLElement)) return;

        // Copy inheritable block-level styles from previous block
        const prevStyle = window.getComputedStyle(prevBlock);
        for (const prop of INHERITABLE_BLOCK_STYLES) {
          const value = prevStyle.getPropertyValue(prop);
          if (value) {
            newBlock.style.setProperty(prop, value);
          }
        }

        editor.save.save();
      });
    }
  };
};

export const createDocumentKeydownHandler = (
  froalaInstance: MutableRefObject<FroalaEditorInstance | null>,
  isFocusEnabled: boolean,
  setHasOverlay: (v: boolean) => void,
) => {
  return (e: KeyboardEvent) => {
    const editor = froalaInstance.current;
    if (!editor || !isFocusEnabled) return;

    // Only handle printable characters
    if (e.key?.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      const editorEl = editor.$el[0];
      if (!editorEl) return;

      // Check if we're in an input, textarea, or other interactive element
      const target = e.target as HTMLElement;
      if (
        ["INPUT", "TEXTAREA", "EM-EMOJI-PICKER"].includes(target.nodeName) ||
        target.closest(COLLAPSIBLE_TEXT_SELECTOR) ||
        target.closest(LIVEBLOCKS_COMMENTS_SELECTOR) ||
        target.closest("[draggable]") ||
        target.closest('[data-rich-text-focus-ignore="true"]') ||
        // Prevent focus when dialogs are open
        DIALOG_IDS_TO_IGNORE.some((id) => document.getElementById(id))
      ) {
        return;
      }

      // Check if we're in a button
      const composedPath = e.composedPath();
      if (
        composedPath.find(
          (c) => c instanceof HTMLElement && c.nodeName === "BUTTON",
        )
      ) {
        return;
      }

      if (!editorEl.contains(document.activeElement)) {
        editor.events?.focus();
        setHasOverlay(false);
      }
    }
  };
};
